import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {User, FileText, Award, Eye, CreditCard, MessageSquare, Settings, LogOut, Upload, Check, X, Camera, Download, Trash2, AlertTriangle, Lock, Key, ExternalLink, CheckCircle, XCircle, Clock, AlertCircle, Calendar, Maximize2, Globe, Search, MapPin} from 'lucide-react'
import DashboardSwitcher from "../components/DashboardSwitcher"
import ViewingBanner from "../components/ViewingBanner"
import { getEffectiveUserId } from "../lib/impersonationHelper"

export default function NurseDashboard() {
  const [activeTab, setActiveTab] = useState("overview")
  const [profile, setProfile] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchProfile = async () => {
      const user = lumi.auth.user
      if (!user) {
        navigate("/auth")
        return
      }

      try {
        // Get effective userId (impersonated or real)
        const effectiveUserId = getEffectiveUserId(user.userId)
        
        const profiles = await lumi.entities.UserProfiles.list({
          filter: { userId: effectiveUserId }
        })
        if (profiles.list.length > 0) {
          setProfile(profiles.list[0])
        }
      } catch (error) {
        console.error(error)
        toast.error("Failed to load profile")
      } finally {
        setIsLoading(false)
      }
    }
    fetchProfile()
  }, [navigate])

  useEffect(() => {
    const fetchUnreadCount = async () => {
      const user = lumi.auth.user
      if (!user) return

      try {
        // Get effective userId (impersonated or real)
        const effectiveUserId = getEffectiveUserId(user.userId)
        
        const { list } = await lumi.entities.MessageThreads.list({
          filter: {
            participants: { $in: [effectiveUserId] },
            unreadCount: { $gt: 0 }
          }
        })
        const total = list.reduce((sum, thread) => sum + (thread.unreadCount || 0), 0)
        setUnreadCount(total)
      } catch (error) {
        console.error("Failed to fetch unread count:", error)
      }
    }
    fetchUnreadCount()
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  const menuItems = [
    { id: "overview", label: "Overview", icon: User },
    { id: "profile", label: "Profile", icon: User },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "certificates", label: "Certificates", icon: Award },
    { id: "destinations", label: "Destinations", icon: Globe },
    { id: "visibility", label: "Visibility", icon: Eye },
    { id: "payments", label: "Payments", icon: CreditCard },
    { id: "messages", label: "Messages", icon: MessageSquare, badge: unreadCount },
    { id: "settings", label: "Settings", icon: Settings }
  ]

  useEffect(() => {
    if (activeTab === "messages") {
      navigate("/messages")
    }
  }, [activeTab, navigate])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Viewing Banner (shows when impersonating) */}
      <ViewingBanner />
      
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://i.imghippo.com/files/KBd1355nw.png"
              alt="Nursing Hive"
              className="w-10 h-auto"
            />
            <h1 className="text-xl font-bold text-gray-900">Nurse Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Completion Progress Bar */}
        <ProfileCompletionProgress profile={profile} onNavigate={(tab) => setActiveTab(tab)} />
        
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <nav className="bg-white rounded-lg shadow-sm p-4 space-y-2">
              {menuItems.map(item => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.id === "messages") {
                        navigate("/dashboard/nurse/messages")
                      } else {
                        setActiveTab(item.id)
                      }
                    }}
                    aria-current={activeTab === item.id ? "page" : undefined}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl transition-all ${
                      activeTab === item.id
                        ? "bg-emerald-50 text-emerald-700 font-semibold ring-1 ring-emerald-200 shadow-sm"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </div>
                    {item.badge && item.badge > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm p-6">
              {activeTab === "overview" && <OverviewTab profile={profile} setProfile={setProfile} onNavigate={(tab) => setActiveTab(tab)} />}
              {activeTab === "profile" && <ProfileTabWithTabs profile={profile} setProfile={setProfile} />}
              {activeTab === "documents" && <DocumentsTab />}
              {activeTab === "certificates" && <CertificatesTab profile={profile} setProfile={setProfile} />}
              {activeTab === "destinations" && <DestinationsTab />}
              {activeTab === "visibility" && <VisibilityTab profile={profile} setProfile={setProfile} />}
              {activeTab === "payments" && <PaymentsTab />}
              {activeTab === "messages" && <MessagesTab />}
              {activeTab === "settings" && <SettingsTab />}
            </div>
          </main>
        </div>
      </div>
    </div>
  )
}

// Profile Tab with 5 Sub-tabs
function ProfileTabWithTabs({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [activeSubTab, setActiveSubTab] = useState("personal")
  const subTabs = [
    { id: "personal", label: "Personal" },
    { id: "education", label: "Education" },
    { id: "experience", label: "Experience" },
    { id: "specialties", label: "Specialties" },
    { id: "addresses", label: "Addresses" }
  ]

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Your Profile</h2>
      <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
        {subTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            className={`px-4 py-2 font-medium whitespace-nowrap transition-colors ${
              activeSubTab === tab.id
                ? "text-emerald-600 border-b-2 border-emerald-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {activeSubTab === "personal" && <PersonalInfoTab profile={profile} setProfile={setProfile} />}
      {activeSubTab === "education" && <EducationTab profile={profile} setProfile={setProfile} />}
      {activeSubTab === "experience" && <ExperienceTab profile={profile} setProfile={setProfile} />}
      {activeSubTab === "specialties" && <SpecialtiesTab profile={profile} setProfile={setProfile} />}
      {activeSubTab === "addresses" && <AddressesTab profile={profile} setProfile={setProfile} />}
    </div>
  )
}

// Personal Info Tab
function PersonalInfoTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [formData, setFormData] = useState({
    fullName: profile?.fullName || "",
    email: profile?.email || "",
    dateOfBirth: profile?.dateOfBirth || "",
    gender: profile?.gender || "",
    nationality: profile?.nationality || "",
    countryOfResidence: profile?.countryOfResidence || "",
    maritalStatus: profile?.maritalStatus || "",
    nationalIdType: profile?.nationalIdType || "",
    nationalIdNumber: profile?.nationalIdNumber || "",
    passportNumber: profile?.passportNumber || "",
    passportExpiryDate: profile?.passportExpiryDate || "",
    destinationCountry: profile?.destinationCountry || "",
    visaStatus: profile?.visaStatus || "",
    phoneNumber: profile?.phoneNumber || "",
    whatsappNumber: profile?.whatsappNumber || "",
    profilePhotoUrl: profile?.profilePhotoUrl || ""
  })
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
    "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon",
    "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
    "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
    "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
    "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
    "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan",
    "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho",
    "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali",
    "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
    "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
    "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
    "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
    "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia",
    "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
    "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
    "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda",
    "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City",
    "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ]

  const handlePhoneChange = (field: "phoneNumber" | "whatsappNumber", value: string) => {
    // Auto-add + if user starts typing without it
    if (value.length > 0 && !value.startsWith("+") && !value.startsWith("00")) {
      value = "+" + value
    }
    setFormData({...formData, [field]: value})
  }

  const calculateAge = (dob: string): number => {
    if (!dob) return 0
    const today = new Date()
    const birthDate = new Date(dob)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  const validatePassportExpiry = (expiryDate: string): boolean => {
    if (!expiryDate) return true
    const today = new Date()
    const expiry = new Date(expiryDate)
    const sixMonthsFromNow = new Date(today.setMonth(today.getMonth() + 6))
    return expiry >= sixMonthsFromNow
  }

  const getVisaOptions = (destination: string) => {
    switch (destination) {
      case "USA":
        return ["F-1 (Student)", "H-1B (Work)", "J-1 (Exchange)", "Green Card", "US Citizen", "Awaiting Processing", "Other"]
      case "UK":
        return ["Tier 2 (Work)", "Skilled Worker Visa", "Student Visa", "ILR (Indefinite Leave)", "UK Citizen", "Awaiting Processing", "Other"]
      case "GCC":
        return ["Visit Visa", "Work/Residence Visa", "Iqama (KSA)", "GCC Citizen", "Awaiting Processing", "Other"]
      case "Canada":
        return ["Study Permit", "Work Permit", "Permanent Resident", "Canadian Citizen", "Awaiting Processing", "Other"]
      default:
        return ["On Visit Visa", "On Work Visa", "On Student Visa", "Permanent Resident", "Citizen", "Awaiting Processing", "No Visa Required", "Other"]
    }
  }

  const handleSave = async () => {
    const errors: Record<string, string> = {}

    // Validate Date of Birth (must be 18+)
    if (formData.dateOfBirth) {
      const age = calculateAge(formData.dateOfBirth)
      if (age < 18) {
        errors.dateOfBirth = "You must be at least 18 years old"
      }
    }

    // Validate Passport Expiry (must be >= 6 months from today)
    if (formData.passportExpiryDate && !validatePassportExpiry(formData.passportExpiryDate)) {
      errors.passportExpiryDate = "Passport must be valid for at least 6 months from today"
    }

    // Validate phone numbers
    if (formData.phoneNumber && !formData.phoneNumber.startsWith("+") && !formData.phoneNumber.startsWith("00")) {
      errors.phoneNumber = "Phone number must start with + or 00 (country code required)"
    }
    if (formData.whatsappNumber && !formData.whatsappNumber.startsWith("+") && !formData.whatsappNumber.startsWith("00")) {
      errors.whatsappNumber = "WhatsApp number must start with + or 00 (country code required)"
    }

    // TODO: Add real-time uniqueness checks when backend ready:
    // - Check if email already exists (excluding current user)
    // - Check if nationalIdNumber already exists
    // - Check if passportNumber already exists

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      toast.error("Please fix validation errors")
      return
    }

    setValidationErrors({})
    setSaving(true)
    try {
      const updated = await lumi.entities.UserProfiles.update(profile._id, formData)
      setProfile(updated)
      toast.success("Personal info saved successfully")
    } catch (error) {
      toast.error("Failed to save personal info")
    } finally {
      setSaving(false)
    }
  }

  const handlePhotoUpload = async (file: File) => {
    setUploadingPhoto(true)
    try {
      const uploaded = await lumi.tools.file.upload([file])
      if (uploaded[0].fileUrl) {
        setFormData({...formData, profilePhotoUrl: uploaded[0].fileUrl})
        const updated = await lumi.entities.UserProfiles.update(profile._id, {
          profilePhotoUrl: uploaded[0].fileUrl
        })
        setProfile(updated)
        toast.success("Profile photo updated")
      }
    } catch (error) {
      toast.error("Failed to upload photo")
    } finally {
      setUploadingPhoto(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Profile Photo Section */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-100">
        <div className="flex items-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden">
              {formData.profilePhotoUrl ? (
                <img src={formData.profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <User className="w-12 h-12 text-white" />
                </div>
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 bg-emerald-600 text-white p-2 rounded-full shadow-lg hover:bg-emerald-700 cursor-pointer">
              <Camera className="w-4 h-4" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) handlePhotoUpload(file)
                }}
                disabled={uploadingPhoto}
              />
            </label>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Profile Photo</h3>
            <p className="text-sm text-gray-600">Upload a professional photo (JPG, PNG up to 5MB)</p>
            {uploadingPhoto && <p className="text-sm text-emerald-600 mt-2">Uploading...</p>}
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
        <input type="text" value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
          <input 
            type="date" 
            value={formData.dateOfBirth} 
            onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})} 
            className={`w-full px-3 py-2 border rounded-lg ${validationErrors.dateOfBirth ? "border-red-500" : "border-gray-300"}`}
            required 
          />
          {validationErrors.dateOfBirth && <p className="text-red-600 text-xs mt-1">{validationErrors.dateOfBirth}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
          <select value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
            <option value="">Select</option>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nationality *</label>
          <select value={formData.nationality} onChange={(e) => setFormData({...formData, nationality: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
            <option value="">Select nationality</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country of Residence *</label>
          <select value={formData.countryOfResidence} onChange={(e) => setFormData({...formData, countryOfResidence: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
            <option value="">Select country</option>
            {countries.map(country => (
              <option key={country} value={country}>{country}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status</label>
        <select value={formData.maritalStatus} onChange={(e) => setFormData({...formData, maritalStatus: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
          <option value="">Select status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="Divorced">Divorced</option>
          <option value="Widowed">Widowed</option>
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">National ID Type *</label>
          <select value={formData.nationalIdType} onChange={(e) => setFormData({...formData, nationalIdType: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required>
            <option value="">Select ID type</option>
            <option value="CNIC">CNIC (Pakistan)</option>
            <option value="Aadhaar">Aadhaar (India)</option>
            <option value="SSN">SSN (USA)</option>
            <option value="NIN">NIN (UK)</option>
            <option value="SIN">SIN (Canada)</option>
            <option value="Emirates ID">Emirates ID (UAE)</option>
            <option value="Iqama">Iqama (Saudi Arabia)</option>
            <option value="National ID">National ID Card</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">National ID Number *</label>
          <input type="text" value={formData.nationalIdNumber} onChange={(e) => setFormData({...formData, nationalIdNumber: e.target.value})} placeholder="Enter ID number" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Passport Number *</label>
          <input type="text" value={formData.passportNumber} onChange={(e) => setFormData({...formData, passportNumber: e.target.value})} placeholder="Enter passport number" className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Passport Expiry Date *</label>
          <input 
            type="date" 
            value={formData.passportExpiryDate} 
            onChange={(e) => setFormData({...formData, passportExpiryDate: e.target.value})} 
            className={`w-full px-3 py-2 border rounded-lg ${validationErrors.passportExpiryDate ? "border-red-500" : "border-gray-300"}`}
            required 
          />
          {validationErrors.passportExpiryDate && <p className="text-red-600 text-xs mt-1">{validationErrors.passportExpiryDate}</p>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Destination Country *</label>
          <select 
            value={formData.destinationCountry} 
            onChange={(e) => setFormData({...formData, destinationCountry: e.target.value, visaStatus: ""})} 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            required
          >
            <option value="">Select destination</option>
            <option value="USA">🇺🇸 USA</option>
            <option value="UK">🇬🇧 UK/Ireland</option>
            <option value="GCC">🇦🇪 GCC (UAE, KSA, Qatar, etc.)</option>
            <option value="Canada">🇨🇦 Canada</option>
            <option value="Other">🌍 Other</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Where do you want to work?</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current Visa Status</label>
          <select 
            value={formData.visaStatus} 
            onChange={(e) => setFormData({...formData, visaStatus: e.target.value})} 
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            disabled={!formData.destinationCountry}
          >
            <option value="">Select visa status</option>
            {getVisaOptions(formData.destinationCountry).map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">{formData.destinationCountry ? `For ${formData.destinationCountry}` : "Select destination first"}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-xs text-gray-500">(with country code)</span>
          </label>
          <input 
            type="tel" 
            placeholder="+92 300 1234567" 
            value={formData.phoneNumber} 
            onChange={(e) => handlePhoneChange("phoneNumber", e.target.value)} 
            className={`w-full px-3 py-2 border rounded-lg ${validationErrors.phoneNumber ? "border-red-500" : "border-gray-300"}`}
          />
          {validationErrors.phoneNumber && <p className="text-red-600 text-xs mt-1">{validationErrors.phoneNumber}</p>}
          <p className="text-xs text-gray-500 mt-1">Start with + or 00 (e.g., +92, +971, +1)</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            WhatsApp Number <span className="text-xs text-gray-500">(with country code)</span>
          </label>
          <input 
            type="tel" 
            placeholder="+92 300 1234567" 
            value={formData.whatsappNumber} 
            onChange={(e) => handlePhoneChange("whatsappNumber", e.target.value)} 
            className={`w-full px-3 py-2 border rounded-lg ${validationErrors.whatsappNumber ? "border-red-500" : "border-gray-300"}`}
          />
          {validationErrors.whatsappNumber && <p className="text-red-600 text-xs mt-1">{validationErrors.whatsappNumber}</p>}
          <p className="text-xs text-gray-500 mt-1">Start with + or 00 (e.g., +92, +971, +1)</p>
        </div>
      </div>
      <button onClick={handleSave} disabled={saving} className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
        {saving ? "Saving..." : "Save Personal Info"}
      </button>
    </div>
  )
}

// Education Tab with Full CRUD
function EducationTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [educationRecords, setEducationRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchEducation()
  }, [page])

  const fetchEducation = async () => {
    const user = lumi.auth.user
    if (!user) return
    try {
      const { list, total } = await lumi.entities.Education.list({
        filter: { userId: user.userId, deletedAt: null },
        sort: { startDate: -1 },
        limit: 10,
        skip: (page - 1) * 10
      })
      setEducationRecords(list)
      setTotalPages(Math.ceil(total / 10))
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this education record?")) return
    try {
      await lumi.entities.Education.update(id, {
        deletedAt: new Date().toISOString(),
        updatedBy: lumi.auth.user?.userId
      })
      toast.success("Education record deleted")
      fetchEducation()
    } catch (error) {
      toast.error("Failed to delete record")
    }
  }

  const getDuration = (start: string, end: string | null, status: string) => {
    const startYear = new Date(start).getFullYear()
    if (status !== "Completed" || !end) return `${startYear} - Present`
    const endYear = new Date(end).getFullYear()
    return `${startYear} - ${endYear}`
  }

  if (loading) return <div>Loading education records...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Education History</h2>
        <button
          onClick={() => {
            setEditingRecord(null)
            setShowModal(true)
          }}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
        >
          <Award className="w-4 h-4" />
          Add Education
        </button>
      </div>

      {educationRecords.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-300">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No education records added yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Add your first education record →
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {educationRecords.map(record => {
              const statusColors = {
                "Completed": "bg-green-100 text-green-800",
                "In Progress": "bg-blue-100 text-blue-800",
                "Not Completed": "bg-gray-100 text-gray-600"
              }
              return (
                <div key={record._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{record.educationType}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[record.completionStatus as keyof typeof statusColors]}`}>
                          {record.completionStatus}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{record.institution} • {record.country}</p>
                      <p className="text-sm text-gray-600">Field: {record.fieldOfStudy}</p>
                      <p className="text-xs text-gray-500 mt-1">{getDuration(record.startDate, record.endDate, record.completionStatus)}</p>
                      {record.gpa && <p className="text-sm text-gray-600 mt-1">GPA: {record.gpa.toFixed(1)}/4.0</p>}
                      {record.honors && <p className="text-sm text-emerald-600 mt-1">🏆 {record.honors}</p>}
                      {record.notes && <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-2">{record.notes}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingRecord(record)
                          setShowModal(true)
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <Settings className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(record._id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <EducationModal
          record={editingRecord}
          onClose={() => {
            setShowModal(false)
            setEditingRecord(null)
          }}
          onSave={() => {
            fetchEducation()
            setShowModal(false)
            setEditingRecord(null)
          }}
        />
      )}
    </div>
  )
}

function EducationModal({ record, onClose, onSave }: { record?: any; onClose: () => void; onSave: () => void }) {
  const [formData, setFormData] = useState({
    educationType: record?.educationType || "",
    institution: record?.institution || "",
    country: record?.country || "",
    fieldOfStudy: record?.fieldOfStudy || "",
    startDate: record?.startDate || "",
    endDate: record?.endDate || "",
    completionStatus: record?.completionStatus || "In Progress",
    gpa: record?.gpa || "",
    honors: record?.honors || "",
    notes: record?.notes || ""
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const educationTypes = ["Diploma in Nursing", "Post-RN BSN", "BSc Nursing", "MSN", "DNP", "PhD in Nursing", "Other"]
  const fields = ["General Nursing", "Critical Care/ICU", "Pediatric Nursing", "Psychiatric/Mental Health", "Emergency/Trauma", "Maternal & Child Health", "Community Health", "Geriatric Nursing", "Oncology", "Cardiology", "Other"]
  const countries = ["Pakistan", "India", "Philippines", "Bangladesh", "Nepal", "United States", "United Kingdom", "Canada", "Australia", "United Arab Emirates", "Saudi Arabia", "Other"]
  const honorsOptions = ["Pass", "Merit", "Distinction", "High Distinction", "Cum Laude", "Magna Cum Laude", "Summa Cum Laude"]

  const handleSave = async () => {
    const newErrors: Record<string, string> = {}
    if (!formData.educationType) newErrors.educationType = "Required"
    if (!formData.institution) newErrors.institution = "Required"
    if (!formData.country) newErrors.country = "Required"
    if (!formData.fieldOfStudy) newErrors.fieldOfStudy = "Required"
    if (!formData.startDate) newErrors.startDate = "Required"
    if (formData.completionStatus === "Completed" && !formData.endDate) {
      newErrors.endDate = "Required for completed education"
    }
    if (formData.endDate && formData.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = "End date must be after start date"
    }
    if (formData.gpa && (parseFloat(formData.gpa) < 0 || parseFloat(formData.gpa) > 4.0)) {
      newErrors.gpa = "GPA must be between 0.0 and 4.0"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSaving(true)
    try {
      const user = lumi.auth.user
      if (!user) return
      const payload = {
        ...formData,
        userId: user.userId,
        gpa: formData.gpa ? parseFloat(formData.gpa) : null,
        updatedBy: user.userId,
        updatedAt: new Date().toISOString()
      }
      if (record) {
        await lumi.entities.Education.update(record._id, payload)
      } else {
        await lumi.entities.Education.create({ ...payload, createdAt: new Date().toISOString() })
      }
      toast.success(record ? "Education updated" : "Education added")
      onSave()
    } catch (error) {
      toast.error("Failed to save")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{record ? "Edit" : "Add"} Education</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Education Type *</label>
            <select value={formData.educationType} onChange={(e) => setFormData({...formData, educationType: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.educationType ? "border-red-500" : "border-gray-300"}`}>
              <option value="">Select type</option>
              {educationTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.educationType && <p className="text-red-600 text-xs mt-1">{errors.educationType}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Institution *</label>
              <input type="text" value={formData.institution} onChange={(e) => setFormData({...formData, institution: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.institution ? "border-red-500" : "border-gray-300"}`} />
              {errors.institution && <p className="text-red-600 text-xs mt-1">{errors.institution}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
              <select value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.country ? "border-red-500" : "border-gray-300"}`}>
                <option value="">Select country</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.country && <p className="text-red-600 text-xs mt-1">{errors.country}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Field of Study *</label>
            <select value={formData.fieldOfStudy} onChange={(e) => setFormData({...formData, fieldOfStudy: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.fieldOfStudy ? "border-red-500" : "border-gray-300"}`}>
              <option value="">Select field</option>
              {fields.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            {errors.fieldOfStudy && <p className="text-red-600 text-xs mt-1">{errors.fieldOfStudy}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.startDate ? "border-red-500" : "border-gray-300"}`} />
              {errors.startDate && <p className="text-red-600 text-xs mt-1">{errors.startDate}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={formData.completionStatus} onChange={(e) => setFormData({...formData, completionStatus: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="Completed">Completed</option>
                <option value="In Progress">In Progress</option>
                <option value="Not Completed">Not Completed</option>
              </select>
            </div>
          </div>
          {formData.completionStatus === "Completed" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.endDate ? "border-red-500" : "border-gray-300"}`} />
              {errors.endDate && <p className="text-red-600 text-xs mt-1">{errors.endDate}</p>}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GPA (0.0-4.0)</label>
              <input type="number" step="0.01" min="0" max="4" value={formData.gpa} onChange={(e) => setFormData({...formData, gpa: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.gpa ? "border-red-500" : "border-gray-300"}`} />
              {errors.gpa && <p className="text-red-600 text-xs mt-1">{errors.gpa}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Honors/Awards</label>
              <select value={formData.honors} onChange={(e) => setFormData({...formData, honors: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">None</option>
                {honorsOptions.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg"></textarea>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Saving..." : record ? "Update" : "Add"}
          </button>
          <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  )
}

// Experience Tab with Full CRUD
function ExperienceTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [experienceRecords, setExperienceRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchExperience()
  }, [page])

  const fetchExperience = async () => {
    const user = lumi.auth.user
    if (!user) return
    try {
      const { list, total } = await lumi.entities.Experience.list({
        filter: { userId: user.userId, deletedAt: null },
        sort: { startDate: -1 },
        limit: 10,
        skip: (page - 1) * 10
      })
      setExperienceRecords(list)
      setTotalPages(Math.ceil(total / 10))
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this experience record?")) return
    try {
      await lumi.entities.Experience.update(id, {
        deletedAt: new Date().toISOString(),
        updatedBy: lumi.auth.user?.userId
      })
      toast.success("Experience record deleted")
      fetchExperience()
    } catch (error) {
      toast.error("Failed to delete record")
    }
  }

  const getDuration = (start: string, end: string | null, isCurrent: boolean) => {
    const startYear = new Date(start).getFullYear()
    if (isCurrent || !end) return `${startYear} - Present`
    const endYear = new Date(end).getFullYear()
    return `${startYear} - ${endYear}`
  }

  if (loading) return <div>Loading experience records...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Work Experience</h2>
        <button
          onClick={() => {
            setEditingRecord(null)
            setShowModal(true)
          }}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
        >
          <User className="w-4 h-4" />
          Add Experience
        </button>
      </div>

      {experienceRecords.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-300">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No experience records added yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Add your first experience record →
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {experienceRecords.map(record => (
              <div key={record._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{record.position}</h3>
                      {record.isCurrentRole && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                          Current Role
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{record.facility}</p>
                    <p className="text-sm text-gray-600">Specialization: {record.specialization}</p>
                    <p className="text-sm text-gray-600">Type: {record.employmentType}</p>
                    <p className="text-xs text-gray-500 mt-1">{getDuration(record.startDate, record.endDate, record.isCurrentRole)}</p>
                    <p className="text-xs text-gray-500">📍 {record.country}{record.state ? ` - ${record.state}` : ""}</p>
                    {record.description && <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-2">{record.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingRecord(record)
                        setShowModal(true)
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(record._id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <ExperienceModal
          record={editingRecord}
          onClose={() => {
            setShowModal(false)
            setEditingRecord(null)
          }}
          onSave={() => {
            fetchExperience()
            setShowModal(false)
            setEditingRecord(null)
          }}
        />
      )}
    </div>
  )
}

function ExperienceModal({ record, onClose, onSave }: { record?: any; onClose: () => void; onSave: () => void }) {
  const [formData, setFormData] = useState({
    position: record?.position || "",
    facility: record?.facility || "",
    country: record?.country || "",
    state: record?.state || "",
    specialization: record?.specialization || "",
    employmentType: record?.employmentType || "Full-time",
    startDate: record?.startDate || "",
    endDate: record?.endDate || "",
    isCurrentRole: record?.isCurrentRole || false,
    description: record?.description || ""
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const specializations = ["ICU/Critical Care", "Emergency/ER", "Operating Room", "Pediatrics", "Neonatal/NICU", "Maternal & Child Health", "Labor & Delivery", "Postpartum Care", "Community Health", "Psychiatric/Mental Health", "Geriatrics", "Oncology", "Cardiology", "Cardiovascular", "Nephrology/Dialysis", "Infectious Disease", "Wound Care", "Palliative/Hospice Care", "Rehabilitation", "Home Health", "Public Health", "Occupational Health", "School Nursing", "Travel Nursing", "Flight Nursing", "Nurse Manager/Leadership", "Education/Training", "Research", "Informatics", "Other"]
  const employmentTypes = ["Full-time", "Part-time", "Contract", "Temporary", "Volunteer"]
  const countries = ["Pakistan", "India", "Philippines", "Bangladesh", "Nepal", "United States", "United Kingdom", "Canada", "Australia", "United Arab Emirates", "Saudi Arabia", "Kuwait", "Qatar", "Bahrain", "Oman"]

  const handleSave = async () => {
    const newErrors: Record<string, string> = {}
    if (!formData.position) newErrors.position = "Required"
    if (!formData.facility) newErrors.facility = "Required"
    if (!formData.country) newErrors.country = "Required"
    if (!formData.specialization) newErrors.specialization = "Required"
    if (!formData.startDate) newErrors.startDate = "Required"
    if (!formData.isCurrentRole && !formData.endDate) {
      newErrors.endDate = "Required for past roles"
    }
    if (formData.endDate && formData.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = "End date must be after start date"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSaving(true)
    try {
      const user = lumi.auth.user
      if (!user) return
      const payload = {
        ...formData,
        userId: user.userId,
        updatedBy: user.userId,
        updatedAt: new Date().toISOString()
      }
      if (record) {
        await lumi.entities.Experience.update(record._id, payload)
      } else {
        await lumi.entities.Experience.create({ ...payload, createdAt: new Date().toISOString() })
      }
      toast.success(record ? "Experience updated" : "Experience added")
      onSave()
    } catch (error) {
      toast.error("Failed to save")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{record ? "Edit" : "Add"} Experience</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title / Position *</label>
            <input type="text" placeholder="e.g., RN - ICU" value={formData.position} onChange={(e) => setFormData({...formData, position: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.position ? "border-red-500" : "border-gray-300"}`} />
            {errors.position && <p className="text-red-600 text-xs mt-1">{errors.position}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facility / Hospital *</label>
              <input type="text" value={formData.facility} onChange={(e) => setFormData({...formData, facility: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.facility ? "border-red-500" : "border-gray-300"}`} />
              {errors.facility && <p className="text-red-600 text-xs mt-1">{errors.facility}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialization *</label>
              <select value={formData.specialization} onChange={(e) => setFormData({...formData, specialization: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.specialization ? "border-red-500" : "border-gray-300"}`}>
                <option value="">Select specialization</option>
                {specializations.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {errors.specialization && <p className="text-red-600 text-xs mt-1">{errors.specialization}</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country *</label>
              <select value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.country ? "border-red-500" : "border-gray-300"}`}>
                <option value="">Select country</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.country && <p className="text-red-600 text-xs mt-1">{errors.country}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State / Province</label>
              <input type="text" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
              <select value={formData.employmentType} onChange={(e) => setFormData({...formData, employmentType: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {employmentTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.startDate ? "border-red-500" : "border-gray-300"}`} />
              {errors.startDate && <p className="text-red-600 text-xs mt-1">{errors.startDate}</p>}
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.isCurrentRole} onChange={(e) => setFormData({...formData, isCurrentRole: e.target.checked, endDate: e.target.checked ? "" : formData.endDate})} className="w-4 h-4 text-emerald-600" />
              <span className="text-gray-700">This is my current role</span>
            </label>
          </div>
          {!formData.isCurrentRole && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.endDate ? "border-red-500" : "border-gray-300"}`} />
              {errors.endDate && <p className="text-red-600 text-xs mt-1">{errors.endDate}</p>}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description / Achievements</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={4} placeholder="Key responsibilities, achievements, skills developed..." className="w-full px-3 py-2 border border-gray-300 rounded-lg"></textarea>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Saving..." : record ? "Update" : "Add"}
          </button>
          <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  )
}

// Specialties Tab with Full CRUD
function SpecialtiesTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [specialtyRecords, setSpecialtyRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState<any>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  useEffect(() => {
    fetchSpecialties()
  }, [page])

  const fetchSpecialties = async () => {
    const user = lumi.auth.user
    if (!user) return
    try {
      const { list, total } = await lumi.entities.Specialties.list({
        filter: { userId: user.userId, deletedAt: null },
        sort: { createdAt: -1 },
        limit: 10,
        skip: (page - 1) * 10
      })
      setSpecialtyRecords(list)
      setTotalPages(Math.ceil(total / 10))
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this specialty?")) return
    try {
      await lumi.entities.Specialties.update(id, {
        deletedAt: new Date().toISOString(),
        updatedBy: lumi.auth.user?.userId
      })
      toast.success("Specialty deleted")
      fetchSpecialties()
    } catch (error) {
      toast.error("Failed to delete specialty")
    }
  }

  const getCertBadgeColor = (level: string) => {
    const colors = {
      "Basic": "bg-gray-100 text-gray-700",
      "Intermediate": "bg-amber-100 text-amber-700",
      "Advanced": "bg-blue-100 text-blue-700",
      "Expert": "bg-purple-100 text-purple-700"
    }
    return colors[level as keyof typeof colors] || colors.Basic
  }

  if (loading) return <div>Loading specialties...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Nursing Specialties</h2>
        <button
          onClick={() => {
            setEditingRecord(null)
            setShowModal(true)
          }}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2"
        >
          <Award className="w-4 h-4" />
          Add Specialty
        </button>
      </div>

      {specialtyRecords.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-300">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No specialties added yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Add your first specialty →
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {specialtyRecords.map(record => (
              <div key={record._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{record.specialty}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getCertBadgeColor(record.certificationLevel)}`}>
                        {record.certificationLevel}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{record.yearsOfExperience} years experience</p>
                    {record.description && <p className="text-sm text-gray-600 mt-2">{record.description}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingRecord(record)
                        setShowModal(true)
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(record._id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <SpecialtyModal
          record={editingRecord}
          onClose={() => {
            setShowModal(false)
            setEditingRecord(null)
          }}
          onSave={() => {
            fetchSpecialties()
            setShowModal(false)
            setEditingRecord(null)
          }}
        />
      )}
    </div>
  )
}

function SpecialtyModal({ record, onClose, onSave }: { record?: any; onClose: () => void; onSave: () => void }) {
  const [formData, setFormData] = useState({
    specialty: record?.specialty || "",
    yearsOfExperience: record?.yearsOfExperience || "",
    certificationLevel: record?.certificationLevel || "Basic",
    description: record?.description || ""
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const specializations = ["ICU/Critical Care", "Emergency/ER", "Operating Room", "Pediatrics", "Neonatal/NICU", "Maternal & Child Health", "Labor & Delivery", "Postpartum Care", "Community Health", "Psychiatric/Mental Health", "Geriatrics", "Oncology", "Cardiology", "Cardiovascular", "Nephrology/Dialysis", "Infectious Disease", "Wound Care", "Palliative/Hospice Care", "Rehabilitation", "Home Health", "Public Health", "Occupational Health", "School Nursing", "Travel Nursing", "Flight Nursing", "Nurse Manager/Leadership", "Education/Training", "Research", "Informatics", "Other"]
  const certLevels = ["Basic", "Intermediate", "Advanced", "Expert"]

  const handleSave = async () => {
    const newErrors: Record<string, string> = {}
    if (!formData.specialty) newErrors.specialty = "Required"
    if (!formData.yearsOfExperience) newErrors.yearsOfExperience = "Required"
    if (parseInt(formData.yearsOfExperience) < 0) newErrors.yearsOfExperience = "Must be 0 or greater"

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSaving(true)
    try {
      const user = lumi.auth.user
      if (!user) return
      const payload = {
        ...formData,
        yearsOfExperience: parseInt(formData.yearsOfExperience),
        userId: user.userId,
        updatedBy: user.userId,
        updatedAt: new Date().toISOString()
      }
      if (record) {
        await lumi.entities.Specialties.update(record._id, payload)
      } else {
        await lumi.entities.Specialties.create({ ...payload, createdAt: new Date().toISOString() })
      }
      toast.success(record ? "Specialty updated" : "Specialty added")
      onSave()
    } catch (error: any) {
      if (error?.message?.includes("duplicate")) {
        toast.error("You already have this specialty. Please edit the existing one.")
      } else {
        toast.error("Failed to save specialty")
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{record ? "Edit" : "Add"} Specialty</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specialty *</label>
            <select value={formData.specialty} onChange={(e) => setFormData({...formData, specialty: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.specialty ? "border-red-500" : "border-gray-300"}`}>
              <option value="">Select specialty</option>
              {specializations.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {errors.specialty && <p className="text-red-600 text-xs mt-1">{errors.specialty}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience *</label>
              <input type="number" min="0" value={formData.yearsOfExperience} onChange={(e) => setFormData({...formData, yearsOfExperience: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.yearsOfExperience ? "border-red-500" : "border-gray-300"}`} />
              {errors.yearsOfExperience && <p className="text-red-600 text-xs mt-1">{errors.yearsOfExperience}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Certification Level</label>
              <select value={formData.certificationLevel} onChange={(e) => setFormData({...formData, certificationLevel: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                {certLevels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} placeholder="Describe your expertise in this area..." className="w-full px-3 py-2 border border-gray-300 rounded-lg"></textarea>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Saving..." : record ? "Update" : "Add"}
          </button>
          <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  )
}

// Addresses Tab
function AddressesTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [formData, setFormData] = useState({
    permanentAddress: profile?.permanentAddress || { line1: "", line2: "", city: "", state: "", postal: "", country: "" },
    presentSameAsPerm: profile?.presentSameAsPerm || false,
    presentAddress: profile?.presentAddress || { line1: "", line2: "", city: "", state: "", postal: "", country: "" }
  })
  const [saving, setSaving] = useState(false)

  const countries = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia",
    "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon",
    "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
    "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
    "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
    "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti",
    "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan",
    "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho",
    "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali",
    "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro",
    "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria",
    "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay",
    "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia",
    "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia",
    "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
    "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania",
    "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda",
    "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City",
    "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
  ]

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await lumi.entities.UserProfiles.update(profile._id, formData)
      setProfile(updated)
      toast.success("Addresses saved")
    } catch (error) {
      toast.error("Failed to save")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Permanent Address</h3>
        <div className="space-y-3">
          <input type="text" placeholder="Address Line 1" value={formData.permanentAddress.line1} onChange={(e) => setFormData({...formData, permanentAddress: {...formData.permanentAddress, line1: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          <input type="text" placeholder="Address Line 2 (Optional)" value={formData.permanentAddress.line2} onChange={(e) => setFormData({...formData, permanentAddress: {...formData.permanentAddress, line2: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="City" value={formData.permanentAddress.city} onChange={(e) => setFormData({...formData, permanentAddress: {...formData.permanentAddress, city: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <input type="text" placeholder="State/Province" value={formData.permanentAddress.state} onChange={(e) => setFormData({...formData, permanentAddress: {...formData.permanentAddress, state: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input type="text" placeholder="Postal Code" value={formData.permanentAddress.postal} onChange={(e) => setFormData({...formData, permanentAddress: {...formData.permanentAddress, postal: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <select 
              value={formData.permanentAddress.country} 
              onChange={(e) => setFormData({...formData, permanentAddress: {...formData.permanentAddress, country: e.target.value}})} 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Select Country</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={formData.presentSameAsPerm} onChange={(e) => setFormData({...formData, presentSameAsPerm: e.target.checked})} className="w-4 h-4 text-emerald-600" />
        <span className="text-gray-700">Present address is same as permanent</span>
      </label>
      {!formData.presentSameAsPerm && (
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Present Address</h3>
          <div className="space-y-3">
            <input type="text" placeholder="Address Line 1" value={formData.presentAddress.line1} onChange={(e) => setFormData({...formData, presentAddress: {...formData.presentAddress, line1: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <input type="text" placeholder="Address Line 2 (Optional)" value={formData.presentAddress.line2} onChange={(e) => setFormData({...formData, presentAddress: {...formData.presentAddress, line2: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="City" value={formData.presentAddress.city} onChange={(e) => setFormData({...formData, presentAddress: {...formData.presentAddress, city: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <input type="text" placeholder="State/Province" value={formData.presentAddress.state} onChange={(e) => setFormData({...formData, presentAddress: {...formData.presentAddress, state: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input type="text" placeholder="Postal Code" value={formData.presentAddress.postal} onChange={(e) => setFormData({...formData, presentAddress: {...formData.presentAddress, postal: e.target.value}})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <select 
                value={formData.presentAddress.country} 
                onChange={(e) => setFormData({...formData, presentAddress: {...formData.presentAddress, country: e.target.value}})} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
      <button onClick={handleSave} disabled={saving} className="bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
        {saving ? "Saving..." : "Save Addresses"}
      </button>
    </div>
  )
}

// Overview Tab with Profile Photo, Completion, Verification Badges, Next Steps
function OverviewTab({ profile, setProfile, onNavigate }: { profile: any; setProfile: (p: any) => void; onNavigate: (tab: string) => void }) {
  const [showPhotoModal, setShowPhotoModal] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [documents, setDocuments] = useState<any[]>([])
  const [destinationPreferences, setDestinationPreferences] = useState<any>(null)

  useEffect(() => {
    const fetchData = async () => {
      const user = lumi.auth.user
      if (!user) return
      try {
        const [docsRes, destRes] = await Promise.all([
          lumi.entities.Documents.list({ filter: { userId: user.userId } }),
          lumi.entities.DestinationPreferences.list({ filter: { userId: user.userId } })
        ])
        setDocuments(docsRes.list)
        if (destRes.list.length > 0) {
          setDestinationPreferences(destRes.list[0])
        }
      } catch (error) {
        console.error(error)
      }
    }
    fetchData()
  }, [])

  const handlePhotoUpload = async (file: File) => {
    setUploadingPhoto(true)
    try {
      const uploaded = await lumi.tools.file.upload([file])
      if (uploaded[0].fileUrl) {
        const updated = await lumi.entities.UserProfiles.update(profile._id, {
          profilePhotoUrl: uploaded[0].fileUrl
        })
        setProfile(updated)
        toast.success("Profile photo updated")
        setShowPhotoModal(false)
      }
    } catch (error) {
      toast.error("Failed to upload photo")
    } finally {
      setUploadingPhoto(false)
    }
  }

  // Calculate completion
  const checklist = {
    personal: !!(profile?.fullName && profile?.country && profile?.phoneNumber),
    education: !!(profile?.educationLevels?.length),
    specialties: !!(profile?.specialties?.length),
    addresses: !!(profile?.permanentAddress?.line1 && profile?.permanentAddress?.city),
    documents: documents.filter(d => d.status === "verified").length > 0,
    certificates: false, // placeholder
    destinations: !!(destinationPreferences?.selectedCountries?.length > 0)
  }
  const completed = Object.values(checklist).filter(Boolean).length
  const total = Object.keys(checklist).length
  const percent = Math.round((completed / total) * 100)

  // Verification statuses
  const nclexDoc = documents.find(d => d.docType === "nclex")
  const cgfnsDoc = documents.find(d => d.docType === "cgfns")
  const dataflowDoc = documents.find(d => d.docType === "dataflow")
  const englishDoc = documents.find(d => d.docType === "english")

  const verifiedDocsCount = documents.filter(d => d.status === "verified").length

  // Smart next steps
  const getNextSteps = () => {
    const steps = []
    if (!checklist.personal) steps.push({ text: "Complete your personal information", link: "profile" })
    if (!checklist.education) steps.push({ text: "Add your education details", link: "profile" })
    if (!checklist.specialties) steps.push({ text: "Select your nursing specialties", link: "profile" })
    if (!checklist.addresses) steps.push({ text: "Add your address information", link: "profile" })
    if (!checklist.documents) steps.push({ text: "Upload required documents", link: "documents" })
    if (!checklist.destinations) steps.push({ text: "Select your destination countries", link: "destinations" })
    if (steps.length === 0) steps.push({ text: "Review and enable recruiter visibility", link: "visibility" })
    return steps.slice(0, 3)
  }

  return (
    <div className="space-y-6">
      {/* Profile Header Card */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-6 border border-emerald-100">
        <div className="flex items-start gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden">
              {profile?.profilePhotoUrl ? (
                <img src={profile.profilePhotoUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                  <User className="w-12 h-12 text-white" />
                </div>
              )}
            </div>
            <button
              onClick={() => setShowPhotoModal(true)}
              className="absolute -bottom-1 -right-1 bg-emerald-600 text-white p-2 rounded-full shadow-lg hover:bg-emerald-700"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">{profile?.fullName || "Complete Your Profile"}</h2>
            <p className="text-gray-600 text-sm mt-1">ID: NH-{new Date().getFullYear()}-{profile?._id?.slice(-6) || "000000"}</p>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-700">
              {profile?.country && <span className="flex items-center gap-1">🌍 {profile.country}</span>}
              {profile?.experienceYears && <span>• {profile.experienceYears} years experience</span>}
              {profile?.specialties?.length > 0 && <span>• {profile.specialties.slice(0, 3).join(", ")}</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Profile Completion */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Profile Completion</h3>
          <span className="text-2xl font-bold text-emerald-600">{percent}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all" style={{width: `${percent}%`}}></div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          <div className={`flex items-center gap-2 ${checklist.personal ? "text-green-600" : "text-gray-400"}`}>
            {checklist.personal ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Personal Info</span>
          </div>
          <div className={`flex items-center gap-2 ${checklist.education ? "text-green-600" : "text-gray-400"}`}>
            {checklist.education ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Education</span>
          </div>
          <div className={`flex items-center gap-2 ${checklist.specialties ? "text-green-600" : "text-gray-400"}`}>
            {checklist.specialties ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Specialties</span>
          </div>
          <div className={`flex items-center gap-2 ${checklist.addresses ? "text-green-600" : "text-gray-400"}`}>
            {checklist.addresses ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Address</span>
          </div>
          <div className={`flex items-center gap-2 ${checklist.documents ? "text-green-600" : "text-gray-400"}`}>
            {checklist.documents ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Documents</span>
          </div>
          <div className={`flex items-center gap-2 ${checklist.certificates ? "text-green-600" : "text-gray-400"}`}>
            {checklist.certificates ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Certificates</span>
          </div>
          <div className={`flex items-center gap-2 ${checklist.destinations ? "text-green-600" : "text-gray-400"}`}>
            {checklist.destinations ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            <span>Destinations</span>
          </div>
        </div>
      </div>

      {/* Verification Badges */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`rounded-lg p-4 border-2 ${nclexDoc?.status === "verified" ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">NCLEX</span>
            {nclexDoc?.status === "verified" ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-gray-400" />}
          </div>
          <p className="text-xs text-gray-600">{nclexDoc?.status === "verified" ? "Verified" : nclexDoc?.status === "pending" ? "Pending" : "Not Uploaded"}</p>
        </div>
        <div className={`rounded-lg p-4 border-2 ${cgfnsDoc?.status === "verified" ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">CGFNS</span>
            {cgfnsDoc?.status === "verified" ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-gray-400" />}
          </div>
          <p className="text-xs text-gray-600">{cgfnsDoc?.status === "verified" ? "Verified" : cgfnsDoc?.status === "pending" ? "Pending" : "Not Uploaded"}</p>
        </div>
        <div className={`rounded-lg p-4 border-2 ${dataflowDoc?.status === "verified" ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">Dataflow</span>
            {dataflowDoc?.status === "verified" ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-gray-400" />}
          </div>
          <p className="text-xs text-gray-600">{dataflowDoc?.status === "verified" ? "Verified" : dataflowDoc?.status === "pending" ? "Pending" : "Not Uploaded"}</p>
        </div>
        <div className={`rounded-lg p-4 border-2 ${englishDoc?.status === "verified" ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">English Test</span>
            {englishDoc?.status === "verified" ? <CheckCircle className="w-5 h-5 text-green-600" /> : <Clock className="w-5 h-5 text-gray-400" />}
          </div>
          <p className="text-xs text-gray-600">{englishDoc?.status === "verified" ? "Verified" : englishDoc?.status === "pending" ? "Pending" : "Not Uploaded"}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
          <div className="text-3xl font-bold text-blue-600">{profile?.experienceYears || 0}</div>
          <div className="text-sm text-blue-700 mt-1">Years Experience</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-100">
          <div className="text-3xl font-bold text-purple-600">{profile?.specialties?.length || 0}</div>
          <div className="text-sm text-purple-700 mt-1">Specialties</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
          <div className="text-3xl font-bold text-green-600">{verifiedDocsCount}</div>
          <div className="text-sm text-green-700 mt-1">Verified Docs</div>
        </div>
        <div className="bg-teal-50 rounded-lg p-4 text-center border border-teal-100">
          <div className="text-3xl font-bold text-teal-600">{destinationPreferences?.selectedCountries?.length || 0}</div>
          <div className="text-sm text-teal-700 mt-1">Destinations</div>
        </div>
      </div>

      {/* Recruiter Visibility */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-6 border border-amber-200">
        <div className="flex items-start gap-4">
          <Eye className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Recruiter Visibility</h3>
            <p className="text-sm text-gray-600 mb-3">Your profile is currently private. Enable visibility when ready to connect with recruiters.</p>
            <button 
              onClick={() => onNavigate("visibility")}
              className="text-sm bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700"
            >
              Manage Visibility
            </button>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      {getNextSteps().length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-emerald-600" />
            Your Next Steps
          </h3>
          <div className="space-y-3">
            {getNextSteps().map((step, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <span className="text-sm text-gray-700">{idx + 1}. {step.text}</span>
                <button 
                  onClick={() => onNavigate(step.link)}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Go →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo Upload Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setShowPhotoModal(false)}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Profile Photo</h3>
              <button onClick={() => setShowPhotoModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block w-full cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-emerald-500 transition-colors">
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">Click to select a photo</p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handlePhotoUpload(file)
                  }}
                  disabled={uploadingPhoto}
                />
              </label>
            </div>
            {uploadingPhoto && (
              <div className="text-center text-sm text-gray-600">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
                Uploading...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function DocumentsTab() {
  const [documents, setDocuments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [bulkMode, setBulkMode] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<{[key: string]: File}>({})
  const [previewDoc, setPreviewDoc] = useState<any>(null)

  useEffect(() => {
    fetchDocuments()
  }, [])

  const fetchDocuments = async () => {
    const user = lumi.auth.user
    if (!user) return
    try {
      const { list } = await lumi.entities.Documents.list({ filter: { userId: user.userId } })
      setDocuments(list)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const docTypes = [
    { type: "license", name: "Nursing License", category: "Credential" },
    { type: "passport", name: "Passport", category: "ID" },
    { type: "nclex", name: "NCLEX Certificate", category: "Exam" },
    { type: "cgfns", name: "CGFNS Certificate", category: "Exam" },
    { type: "dataflow", name: "Dataflow Report", category: "Credential" },
    { type: "english", name: "English Test (IELTS/OET)", category: "Exam" },
    { type: "diploma", name: "Nursing Diploma", category: "Education" },
    { type: "transcript", name: "Academic Transcript", category: "Education" },
    { type: "experience", name: "Experience Letter", category: "Work" },
    { type: "cv", name: "CV/Resume", category: "Other" }
  ]

  const getDocStatus = (type: string) => {
    const doc = documents.find(d => d.docType === type)
    if (!doc) return { status: "missing", doc: null }
    return { status: doc.status, doc }
  }

  const getExpiryWarning = (expiryDate: string) => {
    if (!expiryDate) return null
    const expiry = new Date(expiryDate)
    const today = new Date()
    const daysLeft = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (daysLeft < 0) return { color: "red", text: "Expired", days: daysLeft }
    if (daysLeft <= 30) return { color: "red", text: `Expires in ${daysLeft}d`, days: daysLeft }
    if (daysLeft <= 60) return { color: "orange", text: `Expires in ${daysLeft}d`, days: daysLeft }
    if (daysLeft <= 90) return { color: "yellow", text: `Expires in ${daysLeft}d`, days: daysLeft }
    return { color: "green", text: `Valid (${daysLeft}d left)`, days: daysLeft }
  }

  const handleFileSelect = (type: string, file: File | null) => {
    if (file) {
      setSelectedFiles({...selectedFiles, [type]: file})
    } else {
      const newFiles = {...selectedFiles}
      delete newFiles[type]
      setSelectedFiles(newFiles)
    }
  }

  const handleUpload = async (type: string) => {
    const file = selectedFiles[type]
    if (!file) return
    const user = lumi.auth.user
    if (!user) return

    try {
      const uploaded = await lumi.tools.file.upload([file])
      if (uploaded[0].fileUrl) {
        const docInfo = docTypes.find(d => d.type === type)
        await lumi.entities.Documents.create({
          userId: user.userId,
          docType: type,
          docName: docInfo?.name || type,
          category: docInfo?.category || "Other",
          fileUrl: uploaded[0].fileUrl,
          fileName: file.name,
          status: "pending",
          uploadedAt: new Date().toISOString()
        })
        toast.success(`${docInfo?.name} uploaded`)
        fetchDocuments()
        const newFiles = {...selectedFiles}
        delete newFiles[type]
        setSelectedFiles(newFiles)
      }
    } catch (error) {
      toast.error("Upload failed")
    }
  }

  const handleBulkUpload = async () => {
    const uploads = Object.keys(selectedFiles).map(type => handleUpload(type))
    await Promise.all(uploads)
    setBulkMode(false)
  }

  const uploadedCount = documents.length
  const totalCount = docTypes.length
  const progress = Math.round((uploadedCount / totalCount) * 100)

  if (loading) return <div>Loading documents...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Documents</h2>
        <button onClick={() => setBulkMode(!bulkMode)} className="text-sm text-emerald-600 hover:text-emerald-700">
          {bulkMode ? "Exit Bulk Mode" : "Bulk Upload"}
        </button>
      </div>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Progress: {uploadedCount}/{totalCount} documents</span>
          <span className="text-sm font-medium text-gray-700">{progress}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all" style={{width: `${progress}%`}}></div>
        </div>
      </div>
      {bulkMode && Object.keys(selectedFiles).length > 0 && (
        <button onClick={handleBulkUpload} className="mb-4 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700">
          Upload All ({Object.keys(selectedFiles).length} files)
        </button>
      )}
      <div className="space-y-4">
        {docTypes.map(docType => {
          const { status, doc } = getDocStatus(docType.type)
          const expiry = doc?.expiryDate ? getExpiryWarning(doc.expiryDate) : null
          const statusColors = {
            verified: "bg-green-100 text-green-800",
            pending: "bg-yellow-100 text-yellow-800",
            rejected: "bg-red-100 text-red-800",
            expired: "bg-red-100 text-red-800",
            missing: "bg-gray-100 text-gray-600"
          }
          return (
            <div key={docType.type} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{docType.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[status as keyof typeof statusColors]}`}>
                      {status === "verified" && "✓ Verified"}
                      {status === "pending" && "⏱ Pending"}
                      {status === "rejected" && "✗ Rejected"}
                      {status === "expired" && "✗ Expired"}
                      {status === "missing" && "Not Uploaded"}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{docType.category}</span>
                  </div>
                  {doc && (
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}</p>
                      {expiry && (
                        <p className={`font-medium text-${expiry.color}-600`}>{expiry.text}</p>
                      )}
                      {doc.reviewerNote && (
                        <p className="bg-red-50 text-red-700 p-2 rounded text-xs mt-2">
                          Admin Note: {doc.reviewerNote}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {doc?.fileUrl && (
                    <>
                      <button onClick={() => setPreviewDoc(doc)} className="text-blue-600 hover:text-blue-700">
                        <Maximize2 className="w-5 h-5" />
                      </button>
                      <a href={doc.fileUrl} download className="text-emerald-600 hover:text-emerald-700">
                        <Download className="w-5 h-5" />
                      </a>
                    </>
                  )}
                  <label className="cursor-pointer text-emerald-600 hover:text-emerald-700">
                    <Upload className="w-5 h-5" />
                    <input type="file" className="hidden" onChange={(e) => handleFileSelect(docType.type, e.target.files?.[0] || null)} />
                  </label>
                </div>
              </div>
              {selectedFiles[docType.type] && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-sm text-gray-600">{selectedFiles[docType.type].name}</span>
                  {!bulkMode && (
                    <button onClick={() => handleUpload(docType.type)} className="text-sm bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-700">
                      Upload
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setPreviewDoc(null)}>
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto p-4 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setPreviewDoc(null)} className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600">
              <X className="w-5 h-5" />
            </button>
            {previewDoc.fileUrl?.endsWith(".pdf") ? (
              <iframe src={previewDoc.fileUrl} className="w-full h-[80vh]" />
            ) : (
              <img src={previewDoc.fileUrl} alt={previewDoc.docName} className="max-w-full" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function CertificatesTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [certificates, setCertificates] = useState<any[]>([])
  const [documents, setDocuments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingCert, setEditingCert] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const user = lumi.auth.user
    if (!user) return
    try {
      const [certsRes, docsRes] = await Promise.all([
        lumi.entities.Certificates.list({ filter: { userId: user.userId } }),
        lumi.entities.Documents.list({ filter: { userId: user.userId } })
      ])
      setCertificates(certsRes.list)
      setDocuments(docsRes.list)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const getExpiryStatus = (expiryDate: string) => {
    const expiry = new Date(expiryDate)
    const today = new Date()
    const daysLeft = Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    if (daysLeft < 0) return { color: "red", text: "Expired", days: daysLeft, status: "expired" }
    if (daysLeft <= 30) return { color: "red", text: `Expires in ${daysLeft}d`, days: daysLeft, status: "expiring_soon" }
    if (daysLeft <= 90) return { color: "yellow", text: `Expires in ${daysLeft}d`, days: daysLeft, status: "expiring_soon" }
    return { color: "green", text: `Valid (${daysLeft}d left)`, days: daysLeft, status: "active" }
  }

  if (loading) return <div>Loading certificates...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Professional Certificates</h2>
        <button onClick={() => setShowAddModal(true)} className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 flex items-center gap-2">
          <Award className="w-4 h-4" />
          Add Certificate
        </button>
      </div>
      <p className="text-gray-600 mb-4">Track your professional licenses and certifications. Link to uploaded documents for verification.</p>
      
      {certificates.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-300">
          <Award className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No certificates added yet</p>
          <button onClick={() => setShowAddModal(true)} className="text-emerald-600 hover:text-emerald-700 font-medium">
            Add your first certificate →
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {certificates.map(cert => {
            const expiry = getExpiryStatus(cert.expiryDate)
            const linkedDoc = documents.find(d => d._id === cert.linkedDocumentId)
            return (
              <div key={cert._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{cert.certificateType}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full bg-${expiry.color}-100 text-${expiry.color}-800`}>
                        {expiry.text}
                      </span>
                      {cert.status === "verified" && <CheckCircle className="w-4 h-4 text-green-600" />}
                    </div>
                    <p className="text-sm text-gray-600">License #: {cert.licenseNumber}</p>
                    <p className="text-sm text-gray-600">{cert.issuingOrganization} • {cert.countryOfIssue}</p>
                    <p className="text-xs text-gray-500 mt-1">Issued: {new Date(cert.issueDate).toLocaleDateString()} • Expires: {new Date(cert.expiryDate).toLocaleDateString()}</p>
                    {linkedDoc && (
                      <div className="mt-2 flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded w-fit">
                        <FileText className="w-3 h-3" />
                        Linked: {linkedDoc.docName} ({new Date(linkedDoc.uploadedAt).toLocaleDateString()})
                      </div>
                    )}
                    {!linkedDoc && cert.linkedDocumentId && (
                      <div className="mt-2 text-xs bg-red-50 text-red-700 px-2 py-1 rounded w-fit">
                        ⚠ Document removed — please re-link
                      </div>
                    )}
                  </div>
                  <button onClick={() => setEditingCert(cert)} className="text-gray-400 hover:text-gray-600">
                    <Settings className="w-5 h-5" />
                  </button>
                </div>
                {cert.notes && <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{cert.notes}</p>}
              </div>
            )
          })}
        </div>
      )}
      
      {showAddModal && <CertificateModal onClose={() => setShowAddModal(false)} onSave={fetchData} documents={documents} />}
      {editingCert && <CertificateModal cert={editingCert} onClose={() => setEditingCert(null)} onSave={fetchData} documents={documents} />}
    </div>
  )
}

function CertificateModal({ cert, onClose, onSave, documents }: { cert?: any; onClose: () => void; onSave: () => void; documents: any[] }) {
  const [formData, setFormData] = useState({
    certificateType: cert?.certificateType || "",
    licenseNumber: cert?.licenseNumber || "",
    issuingOrganization: cert?.issuingOrganization || "",
    countryOfIssue: cert?.countryOfIssue || "",
    issueDate: cert?.issueDate || "",
    expiryDate: cert?.expiryDate || "",
    linkedDocumentId: cert?.linkedDocumentId || "",
    notes: cert?.notes || ""
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const certTypes = ["NCLEX-RN", "NCLEX-PN", "CGFNS", "VisaScreen", "Dataflow", "IELTS", "OET", "State License", "Professional Registration", "Specialty Certification", "Other"]

  const handleSave = async () => {
    const newErrors: Record<string, string> = {}
    if (!formData.certificateType) newErrors.certificateType = "Required"
    if (!formData.licenseNumber) newErrors.licenseNumber = "Required"
    if (!formData.issuingOrganization) newErrors.issuingOrganization = "Required"
    if (!formData.countryOfIssue) newErrors.countryOfIssue = "Required"
    if (!formData.issueDate) newErrors.issueDate = "Required"
    if (!formData.expiryDate) newErrors.expiryDate = "Required"
    if (formData.expiryDate && formData.issueDate && new Date(formData.expiryDate) <= new Date(formData.issueDate)) {
      newErrors.expiryDate = "Expiry date must be after issue date"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setSaving(true)
    try {
      const user = lumi.auth.user
      if (!user) return
      const payload = { ...formData, userId: user.userId, status: "pending_verification", createdAt: new Date().toISOString() }
      if (cert) {
        await lumi.entities.Certificates.update(cert._id, payload)
      } else {
        await lumi.entities.Certificates.create(payload)
      }
      toast.success(cert ? "Certificate updated" : "Certificate added")
      onSave()
      onClose()
    } catch (error) {
      toast.error("Failed to save certificate")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{cert ? "Edit" : "Add"} Certificate</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Type *</label>
            <select value={formData.certificateType} onChange={(e) => setFormData({...formData, certificateType: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.certificateType ? "border-red-500" : "border-gray-300"}`}>
              <option value="">Select type</option>
              {certTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {errors.certificateType && <p className="text-red-600 text-xs mt-1">{errors.certificateType}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">License Number *</label>
              <input type="text" value={formData.licenseNumber} onChange={(e) => setFormData({...formData, licenseNumber: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.licenseNumber ? "border-red-500" : "border-gray-300"}`} />
              {errors.licenseNumber && <p className="text-red-600 text-xs mt-1">{errors.licenseNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country of Issue *</label>
              <input type="text" value={formData.countryOfIssue} onChange={(e) => setFormData({...formData, countryOfIssue: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.countryOfIssue ? "border-red-500" : "border-gray-300"}`} />
              {errors.countryOfIssue && <p className="text-red-600 text-xs mt-1">{errors.countryOfIssue}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Organization *</label>
            <input type="text" value={formData.issuingOrganization} onChange={(e) => setFormData({...formData, issuingOrganization: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.issuingOrganization ? "border-red-500" : "border-gray-300"}`} />
            {errors.issuingOrganization && <p className="text-red-600 text-xs mt-1">{errors.issuingOrganization}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date *</label>
              <input type="date" value={formData.issueDate} onChange={(e) => setFormData({...formData, issueDate: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.issueDate ? "border-red-500" : "border-gray-300"}`} />
              {errors.issueDate && <p className="text-red-600 text-xs mt-1">{errors.issueDate}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
              <input type="date" value={formData.expiryDate} onChange={(e) => setFormData({...formData, expiryDate: e.target.value})} className={`w-full px-3 py-2 border rounded-lg ${errors.expiryDate ? "border-red-500" : "border-gray-300"}`} />
              {errors.expiryDate && <p className="text-red-600 text-xs mt-1">{errors.expiryDate}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Link to Document (optional)</label>
            <select value={formData.linkedDocumentId} onChange={(e) => setFormData({...formData, linkedDocumentId: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="">No document linked</option>
              {documents.map(doc => (
                <option key={doc._id} value={doc._id}>{doc.docName} - {new Date(doc.uploadedAt).toLocaleDateString()}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Select a document you've already uploaded in the Documents section</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg"></textarea>
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-600 text-white px-6 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
            {saving ? "Saving..." : cert ? "Update" : "Add"} Certificate
          </button>
          <button onClick={onClose} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  )
}

// Destinations Tab with Regional Grouping and Search
function DestinationsTab() {
  const [preferences, setPreferences] = useState<any>(null)
  const [selectedCountries, setSelectedCountries] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [expandedRegions, setExpandedRegions] = useState<string[]>(["GCC", "North America"])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Country data organized by region
  const regions = {
    "GCC": ["Saudi Arabia", "United Arab Emirates", "Qatar", "Kuwait", "Bahrain", "Oman"],
    "North America": ["United States", "Canada"],
    "Europe": ["United Kingdom", "Ireland", "Germany", "France", "Netherlands", "Switzerland", "Belgium", "Austria", "Sweden", "Norway", "Denmark", "Finland"],
    "Southeast Asia": ["Singapore", "Malaysia", "Philippines", "Thailand", "Indonesia", "Vietnam", "Brunei"],
    "South Asia": ["India", "Pakistan", "Bangladesh", "Sri Lanka", "Nepal", "Maldives"],
    "Middle East": ["Jordan", "Lebanon", "Egypt", "Turkey", "Israel"],
    "Australia/Oceania": ["Australia", "New Zealand", "Papua New Guinea", "Fiji"],
    "Africa": ["South Africa", "Kenya", "Nigeria", "Ghana", "Egypt", "Morocco", "Tanzania"]
  }

  useEffect(() => {
    fetchPreferences()
  }, [])

  const fetchPreferences = async () => {
    const user = lumi.auth.user
    if (!user) return
    try {
      const { list } = await lumi.entities.DestinationPreferences.list({
        filter: { userId: user.userId }
      })
      if (list.length > 0) {
        setPreferences(list[0])
        setSelectedCountries(list[0].selectedCountries || [])
      }
    } catch (error) {
      console.error("Failed to fetch preferences:", error)
    } finally {
      setLoading(false)
    }
  }

  const toggleRegion = (region: string) => {
    setExpandedRegions(prev =>
      prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]
    )
  }

  const toggleCountry = (country: string) => {
    setSelectedCountries(prev =>
      prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
    )
  }

  const removeCountry = (country: string) => {
    setSelectedCountries(prev => prev.filter(c => c !== country))
  }

  const clearAll = () => {
    if (window.confirm("Remove all selected countries?")) {
      setSelectedCountries([])
    }
  }

  const handleSave = async () => {
    const user = lumi.auth.user
    if (!user) return

    setSaving(true)
    try {
      const payload = {
        userId: user.userId,
        selectedCountries,
        updatedAt: new Date().toISOString()
      }

      if (preferences) {
        await lumi.entities.DestinationPreferences.update(preferences._id, payload)
      } else {
        const created = await lumi.entities.DestinationPreferences.create({
          ...payload,
          createdAt: new Date().toISOString()
        })
        setPreferences(created)
      }
      toast.success("Destination preferences saved successfully")
    } catch (error) {
      toast.error("Failed to save preferences")
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  // Filter regions and countries based on search
  const filteredRegions = Object.entries(regions).reduce((acc, [region, countries]) => {
    const matchingCountries = countries.filter(country =>
      country.toLowerCase().includes(searchQuery.toLowerCase())
    )
    if (matchingCountries.length > 0 || region.toLowerCase().includes(searchQuery.toLowerCase())) {
      acc[region] = matchingCountries.length > 0 ? matchingCountries : countries
    }
    return acc
  }, {} as Record<string, string[]>)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Destination Preferences</h2>
        <p className="text-gray-600">Select countries where you're interested in working. This helps recruiters find you.</p>
      </div>

      {/* Popular Destinations Quick-Select */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Globe className="w-5 h-5 text-blue-600" />
          Popular Destinations - Quick Select
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {["United States", "United Kingdom", "Canada", "United Arab Emirates", "Saudi Arabia", "Australia", "Germany", "Singapore"].map(country => (
            <button
              key={country}
              onClick={() => {
                if (!selectedCountries.includes(country)) {
                  setSelectedCountries(prev => [...prev, country])
                  toast.success(`${country} added`)
                }
              }}
              disabled={selectedCountries.includes(country)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedCountries.includes(country)
                  ? "bg-emerald-100 text-emerald-700 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-blue-100 hover:text-blue-700 border border-gray-200"
              }`}
            >
              {selectedCountries.includes(country) ? "✓ " : ""}{country}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search countries or regions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      {/* Selected Countries Pills */}
      {selectedCountries.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Selected Countries ({selectedCountries.length})</h3>
            <button
              onClick={clearAll}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Clear All
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedCountries.map(country => (
              <span
                key={country}
                className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-emerald-300 rounded-full text-sm font-medium text-gray-700"
              >
                <MapPin className="w-3 h-3 text-emerald-600" />
                {country}
                <button
                  onClick={() => removeCountry(country)}
                  className="text-gray-400 hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Regional Sections */}
      <div className="space-y-3">
        {Object.entries(filteredRegions).map(([region, countries]) => (
          <div key={region} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => toggleRegion(region)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-gray-900">{region}</span>
                <span className="text-sm text-gray-500">
                  ({countries.filter(c => selectedCountries.includes(c)).length}/{countries.length})
                </span>
              </div>
              <div className="text-gray-400">
                {expandedRegions.includes(region) ? "▼" : "▶"}
              </div>
            </button>
            
            {expandedRegions.includes(region) && (
              <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {countries.map(country => (
                  <label
                    key={country}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCountries.includes(country)}
                      onChange={() => toggleCountry(country)}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-700">{country}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* No Results */}
      {Object.keys(filteredRegions).length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No countries found matching "{searchQuery}"</p>
        </div>
      )}

      {/* Save Button */}
      <div className="flex gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={saving || selectedCountries.length === 0}
          className="flex-1 bg-emerald-600 text-white px-6 py-3 rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {saving ? "Saving..." : "Save Preferences"}
        </button>
        <button
          onClick={fetchPreferences}
          className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
        >
          Reset
        </button>
      </div>

      {/* Destination-Specific Requirements Guide */}
      {selectedCountries.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-amber-600" />
            Requirements for Your Selected Destinations
          </h3>
          <div className="space-y-4">
            {selectedCountries.includes("United States") && (
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <h4 className="font-semibold text-gray-900 mb-2">🇺🇸 United States</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ NCLEX-RN exam required</li>
                  <li>✓ CGFNS/VisaScreen certificate</li>
                  <li>✓ English test (IELTS/TOEFL/OET)</li>
                  <li>✓ State license application</li>
                  <li>✓ Visa sponsorship (H-1B or Green Card)</li>
                </ul>
              </div>
            )}
            {(selectedCountries.includes("United Kingdom") || selectedCountries.includes("Ireland")) && (
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <h4 className="font-semibold text-gray-900 mb-2">🇬🇧 UK/Ireland</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ NMC/NMBI registration</li>
                  <li>✓ IELTS Academic (minimum 7.0 overall)</li>
                  <li>✓ CBT (Computer-Based Test) pass</li>
                  <li>✓ OSCE (Objective Structured Clinical Exam)</li>
                  <li>✓ Tier 2 work visa or sponsorship</li>
                </ul>
              </div>
            )}
            {(selectedCountries.some(c => ["Saudi Arabia", "United Arab Emirates", "Qatar", "Kuwait", "Bahrain", "Oman"].includes(c))) && (
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <h4 className="font-semibold text-gray-900 mb-2">🇦🇪 GCC Countries</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ Dataflow/Primary Source Verification</li>
                  <li>✓ Good standing certificate</li>
                  <li>✓ Prometric exam (for UAE/KSA)</li>
                  <li>✓ Ministry of Health license</li>
                  <li>✓ Employment visa sponsored by employer</li>
                </ul>
              </div>
            )}
            {selectedCountries.includes("Canada") && (
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <h4 className="font-semibold text-gray-900 mb-2">🇨🇦 Canada</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ NCLEX-RN exam</li>
                  <li>✓ Provincial nursing registration</li>
                  <li>✓ Language test (IELTS/CELBAN)</li>
                  <li>✓ Educational credential assessment</li>
                  <li>✓ Work permit or permanent residency</li>
                </ul>
              </div>
            )}
            {(selectedCountries.includes("Australia") || selectedCountries.includes("New Zealand")) && (
              <div className="bg-white rounded-lg p-4 border border-amber-100">
                <h4 className="font-semibold text-gray-900 mb-2">🇦🇺 Australia/New Zealand</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✓ AHPRA/NCNZ registration</li>
                  <li>✓ IELTS Academic (7.0+ each band)</li>
                  <li>✓ OET (Grade B minimum)</li>
                  <li>✓ Skills assessment</li>
                  <li>✓ Visa subclass 482/189 or work visa</li>
                </ul>
              </div>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-amber-200">
            <button
              onClick={() => navigate("/resources")}
              className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              View detailed guides and checklists →
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">How this helps you:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Recruiters can find you based on your destination preferences</li>
              <li>Get matched with relevant job opportunities</li>
              <li>Update anytime as your plans change</li>
              <li>Receive notifications when recruiters from your destinations view your profile</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

function VisibilityTab({ profile, setProfile }: { profile: any; setProfile: (p: any) => void }) {
  const [isVisible, setIsVisible] = useState(profile?.visibleToRecruiters || false)
  const [isLoading, setIsLoading] = useState(false)

  const handleToggle = async () => {
    // Confirmation dialog
    const action = !isVisible ? "make your profile visible" : "hide your profile"
    const message = !isVisible 
      ? "Recruiters will be able to view and contact you. Continue?"
      : "Recruiters will no longer see your profile. Continue?"
    
    if (!window.confirm(`${message}`)) {
      return
    }

    setIsLoading(true)
    try {
      const updated = await lumi.entities.UserProfiles.update(profile._id, {
        visibleToRecruiters: !isVisible,
        updatedAt: new Date().toISOString()
      })
      setProfile(updated)
      setIsVisible(!isVisible)
      toast.success(`Profile is now ${!isVisible ? "visible" : "hidden"} to recruiters`)
    } catch (error) {
      toast.error("Failed to update visibility")
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Visibility Settings</h2>
      <div className="p-6 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Visible to Recruiters</h3>
            <p className="text-sm text-gray-600 mt-1">
              Allow verified recruiters to view your profile and contact you
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={isLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              isVisible ? "bg-teal-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isVisible ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        <p className="text-sm text-gray-500">
          Note: Profile searchable after payment confirmation and admin verification.
        </p>
      </div>
    </div>
  )
}

function PaymentsTab() {
  const [paymentHistory, setPaymentHistory] = useState<any[]>([])
  const [paymentStatus, setPaymentStatus] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchPaymentData = async () => {
      const user = lumi.auth.user
      if (!user) return

      try {
        const { list } = await lumi.entities.PaymentHistory.list({
          filter: { userId: user.userId },
          sort: { transactionDate: -1 },
          limit: 1
        })
        setPaymentHistory(list)

        // Determine payment status
        if (list.length > 0 && list[0].status === "succeeded") {
          setPaymentStatus({
            isPaid: true,
            amount: list[0].amountCents / 100,
            currency: list[0].currency.toUpperCase(),
            paidDate: list[0].transactionDate,
            invoiceUrl: list[0].stripeInvoiceUrl,
            isGrandfathered: list[0].amountCents === 500 // $5 = grandfathered rate
          })
        } else {
          setPaymentStatus({
            isPaid: false,
            amount: 10, // Default current price
            currency: "USD",
            isGrandfathered: false
          })
        }
      } catch (error) {
        console.error("Failed to fetch payment data:", error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPaymentData()
  }, [])

  const handleCompletePayment = () => {
    toast("Payment integration coming soon", { icon: "💳" })
    // TODO: Integrate Stripe/payment gateway
  }

  if (isLoading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Payments</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Payments</h2>
      <div className="space-y-6">
        {/* Payment Status Card */}
        <div className={`rounded-xl border-2 p-6 ${
          paymentStatus?.isPaid 
            ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-200" 
            : "bg-gradient-to-br from-blue-50 to-teal-50 border-blue-200"
        }`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-xl font-bold text-gray-900">
                  {paymentStatus?.isPaid ? "Active Account" : "Unlock Lifetime Access"}
                </h3>
                {paymentStatus?.isPaid && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-green-600 text-white">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Active ✓
                  </span>
                )}
              </div>
              <p className="text-gray-700 mb-4">
                {paymentStatus?.isPaid 
                  ? "You have lifetime access to Nursing Hive — no recurring charges, no renewals."
                  : "Get lifetime access to all Nursing Hive features with a single one-time payment."
                }
              </p>

              {/* Payment Details Grid */}
              {paymentStatus?.isPaid && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-600 mb-1">Amount Paid</p>
                    <p className="text-2xl font-bold text-gray-900">
                      ${paymentStatus.amount} {paymentStatus.currency}
                    </p>
                    {paymentStatus.isGrandfathered && (
                      <span className="inline-flex items-center px-2 py-0.5 mt-2 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        🎯 Grandfathered Rate
                      </span>
                    )}
                  </div>
                  <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-600 mb-1">Payment Type</p>
                    <p className="text-lg font-bold text-gray-900">Lifetime Access</p>
                    <p className="text-xs text-gray-600 mt-1">(One-time payment)</p>
                  </div>
                  <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-600 mb-1">Payment Date</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {new Date(paymentStatus.paidDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                    <p className="text-xs text-gray-600 mb-1">Next Payment</p>
                    <p className="text-lg font-bold text-green-600">Never</p>
                    <p className="text-xs text-gray-600 mt-1">(No renewals)</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {paymentStatus?.isPaid ? (
                  <>
                    {paymentStatus.invoiceUrl && (
                      <a
                        href={paymentStatus.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm"
                      >
                        <Download className="w-4 h-4" />
                        Download Receipt
                      </a>
                    )}
                    <button
                      onClick={() => toast("Receipt sent to your email", { icon: "📧" })}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-teal-700 border-2 border-teal-600 rounded-lg hover:bg-teal-50 transition-colors font-medium text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Email Receipt
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleCompletePayment}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-bold text-base shadow-md"
                    >
                      <CreditCard className="w-5 h-5" />
                      Complete Payment (${paymentStatus?.amount})
                    </button>
                    <button
                      onClick={() => toast("Pricing details: $10 one-time payment for lifetime access", { icon: "ℹ️" })}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                    >
                      Learn More
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* About Your Payment Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-teal-600" />
            About Your Payment
          </h3>
          <div className="space-y-3 text-gray-700">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">One-Time Payment</p>
                <p className="text-sm text-gray-600">Pay once, access forever. No hidden fees or surprises.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">No Recurring Charges</p>
                <p className="text-sm text-gray-600">Your card will never be charged again after your initial payment.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Lifetime Access</p>
                <p className="text-sm text-gray-600">Full access to all features, updates, and new tools — forever.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">No Renewal Required</p>
                <p className="text-sm text-gray-600">Never worry about subscription renewals or expiration dates.</p>
              </div>
            </div>
            {paymentStatus?.isGrandfathered && (
              <div className="flex items-start gap-3 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Award className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900">Grandfathered Pricing</p>
                  <p className="text-sm text-amber-800">You're locked in at the early adopter rate of $5 — a savings of $5 compared to the current $10 price!</p>
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600 mb-3">
              <strong>Need help?</strong> Contact our support team
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="mailto:hello@nursinghive.com"
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                hello@nursinghive.com
              </a>
              <button
                onClick={() => toast("Support chat coming soon", { icon: "💬" })}
                className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                <MessageSquare className="w-4 h-4" />
                Live Chat
              </button>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-bold text-gray-900">Payment History</h3>
            <p className="text-sm text-gray-600 mt-1">Your one-time payment record</p>
          </div>
          <div className="overflow-x-auto">
            {paymentHistory.length === 0 ? (
              <div className="text-center py-12">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No payment yet</p>
                <p className="text-sm text-gray-500 mt-1">Complete your one-time payment to unlock lifetime access.</p>
                <button
                  onClick={handleCompletePayment}
                  className="mt-4 inline-flex items-center gap-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  <CreditCard className="w-4 h-4" />
                  Complete Payment
                </button>
              </div>
            ) : (
              <div className="p-6">
                {paymentHistory.map((payment) => (
                  <div key={payment._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-teal-300 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Paid
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(payment.transactionDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">{payment.description || "Nursing Hive Lifetime Access"}</p>
                      <p className="text-xs text-gray-600">Transaction ID: {payment._id}</p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-2xl font-bold text-gray-900 mb-2">
                        ${(payment.amountCents / 100).toFixed(2)} {payment.currency.toUpperCase()}
                      </p>
                      {payment.stripeInvoiceUrl && (
                        <a
                          href={payment.stripeInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 font-medium text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Receipt
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Payment Method (Future Enhancement) */}
        {paymentStatus?.isPaid && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Method</h3>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-8 bg-gradient-to-r from-blue-600 to-blue-400 rounded flex items-center justify-center text-white font-bold text-xs">
                  VISA
                </div>
                <div>
                  <p className="font-semibold text-gray-900">•••• •••• •••• 4242</p>
                  <p className="text-xs text-gray-600">Expires 12/2027</p>
                </div>
              </div>
              <button
                onClick={() => toast("Card management coming soon", { icon: "💳" })}
                className="text-teal-600 hover:text-teal-700 font-medium text-sm"
              >
                Update
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Your payment method is stored securely. No further charges will be made.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function MessagesTab() {
  return <div><h2 className="text-2xl font-bold">Messages</h2><p className="text-gray-600 mt-2">View your messages.</p></div>
}

function SettingsTab() {
  const navigate = useNavigate()
  const [certificateAlerts, setCertificateAlerts] = useState(true)
  const [paymentReminders, setPaymentReminders] = useState(true)
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false)
  const [isLoadingReminders, setIsLoadingReminders] = useState(false)
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteConfirmed, setDeleteConfirmed] = useState(false)
  const [deleteFeedback, setDeleteFeedback] = useState("")
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Fetch current settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      const user = lumi.auth.user
      if (!user) return
      try {
        // Fetch user settings from backend
        const response = await lumi.functions.invoke("get-user-settings", {
          method: "GET",
          query: { userId: user.userId }
        })
        if (response.certificateAlerts !== undefined) setCertificateAlerts(response.certificateAlerts)
        if (response.paymentReminders !== undefined) setPaymentReminders(response.paymentReminders)
      } catch (error) {
        // Default to ON if fetch fails
        console.error("Failed to fetch settings:", error)
      }
    }
    fetchSettings()
  }, [])

  const handleCertificateAlertsToggle = async () => {
    const user = lumi.auth.user
    if (!user) return

    const previousValue = certificateAlerts
    // Optimistic update
    setCertificateAlerts(!certificateAlerts)
    setIsLoadingAlerts(true)

    try {
      await lumi.functions.invoke("certificate-alerts", {
        method: "POST",
        body: { userId: user.userId, enabled: !certificateAlerts }
      })
      toast.success(`Certificate alerts ${!certificateAlerts ? "enabled" : "disabled"}`)
    } catch (error) {
      // Revert on error
      setCertificateAlerts(previousValue)
      toast.error("Failed to update certificate alerts")
      console.error(error)
    } finally {
      setIsLoadingAlerts(false)
    }
  }

  const handlePaymentRemindersToggle = async () => {
    const user = lumi.auth.user
    if (!user) return

    const previousValue = paymentReminders
    // Optimistic update
    setPaymentReminders(!paymentReminders)
    setIsLoadingReminders(true)

    try {
      await lumi.functions.invoke("payment-reminders", {
        method: "POST",
        body: { userId: user.userId, enabled: !paymentReminders }
      })
      toast.success(`Payment reminders ${!paymentReminders ? "enabled" : "disabled"}`)
    } catch (error) {
      // Revert on error
      setPaymentReminders(previousValue)
      toast.error("Failed to update payment reminders")
      console.error(error)
    } finally {
      setIsLoadingReminders(false)
    }
  }

  const handleDeactivateAccount = async () => {
    const user = lumi.auth.user
    if (!user) return

    setIsDeactivating(true)
    try {
      await lumi.functions.invoke("deactivate-account", {
        method: "POST",
        body: { userId: user.userId }
      })
      toast.success("Account deactivated successfully")
      // Logout and redirect
      setTimeout(() => {
        lumi.auth.signOut()
        navigate("/")
      }, 1500)
    } catch (error) {
      toast.error("Failed to deactivate account")
      console.error(error)
      setIsDeactivating(false)
    }
  }

  const handleDeleteAccount = async () => {
    const user = lumi.auth.user
    if (!user) return

    if (!deleteConfirmed) {
      toast.error("Please confirm deletion by checking the box")
      return
    }

    setIsDeleting(true)
    try {
      await lumi.functions.invoke("delete-account", {
        method: "POST",
        body: { 
          userId: user.userId, 
          feedback: deleteFeedback 
        }
      })
      toast.success("Deletion request submitted. Admin will review within 7 days.")
      // Logout and redirect
      setTimeout(() => {
        lumi.auth.signOut()
        navigate("/")
      }, 2000)
    } catch (error) {
      toast.error("Failed to submit deletion request")
      console.error(error)
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings</h2>
        <p className="text-gray-600">Manage your account preferences and notifications</p>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4 border-b border-emerald-100">
          <h3 className="text-lg font-bold text-gray-900">Notification Preferences</h3>
          <p className="text-sm text-gray-600 mt-1">Control what notifications you receive</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {/* Certificate Expiry Alerts */}
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Award className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-semibold text-gray-900">Certificate Expiry Alerts</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Get notified 90, 60, and 30 days before your certificates expire
                </p>
              </div>
              <button
                onClick={handleCertificateAlertsToggle}
                disabled={isLoadingAlerts}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  certificateAlerts ? "bg-emerald-600" : "bg-gray-300"
                } ${isLoadingAlerts ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    certificateAlerts ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Payment Reminders */}
          <div className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-semibold text-gray-900">Payment Reminders</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Receive notifications about upcoming charges or failed payments
                </p>
              </div>
              <button
                onClick={handlePaymentRemindersToggle}
                disabled={isLoadingReminders}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  paymentReminders ? "bg-emerald-600" : "bg-gray-300"
                } ${isLoadingReminders ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    paymentReminders ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-white rounded-xl border-2 border-red-200 overflow-hidden">
        <div className="bg-gradient-to-r from-red-50 to-orange-50 px-6 py-4 border-b border-red-200">
          <h3 className="text-lg font-bold text-red-900 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            ⚠️ Delete or Deactivate Account
          </h3>
          <p className="text-sm text-red-700 mt-1">Irreversible actions — proceed with caution</p>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Deactivate Account */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">Deactivate Account</h4>
                <p className="text-sm text-gray-600">
                  Temporarily disable your account. You can reactivate anytime by logging in.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDeactivateModal(true)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm"
            >
              Deactivate Account
            </button>
          </div>

          {/* Request Permanent Deletion */}
          <div className="border-2 border-red-300 rounded-lg p-4 bg-red-50">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1">
                <h4 className="font-semibold text-red-900 mb-1">Request Permanent Deletion</h4>
                <p className="text-sm text-red-700">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
            >
              Request Permanent Deletion
            </button>
          </div>
        </div>
      </div>

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => !isDeactivating && setShowDeactivateModal(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Deactivate Account?</h3>
                <p className="text-sm text-gray-600">This action is reversible</p>
              </div>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                <strong>What happens when you deactivate:</strong>
              </p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Your profile will be hidden from recruiters</li>
                <li>You won't receive any notifications</li>
                <li>Your data remains safe and secure</li>
                <li>You can reactivate anytime by logging in</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeactivateAccount}
                disabled={isDeactivating}
                className="flex-1 bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isDeactivating ? "Deactivating..." : "Yes, Deactivate"}
              </button>
              <button
                onClick={() => setShowDeactivateModal(false)}
                disabled={isDeactivating}
                className="flex-1 px-6 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={() => !isDeleting && setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-red-900">Request Account Deletion?</h3>
                <p className="text-sm text-red-600">This action is permanent</p>
              </div>
            </div>
            
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6">
              <p className="text-sm font-bold text-red-900 mb-2">
                ⚠️ WARNING: This cannot be undone!
              </p>
              <p className="text-sm text-red-800 mb-3">
                <strong>What will be deleted:</strong>
              </p>
              <ul className="text-sm text-red-700 space-y-1 list-disc list-inside mb-3">
                <li>Your profile and all personal information</li>
                <li>All documents, certificates, and credentials</li>
                <li>Education, experience, and specialty records</li>
                <li>Payment history and transaction records</li>
                <li>All messages and conversations</li>
              </ul>
              <p className="text-sm text-red-800">
                <strong>Timeline:</strong> Admin will review within 7 business days. You'll receive confirmation via email.
              </p>
            </div>

            <div className="space-y-4 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={deleteConfirmed}
                  onChange={(e) => setDeleteConfirmed(e.target.checked)}
                  className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                />
                <span className="text-sm text-gray-700">
                  I understand this is permanent and cannot be undone. I want to delete my account and all associated data.
                </span>
              </label>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Optional: Help us improve (why are you leaving?)
                </label>
                <textarea
                  value={deleteFeedback}
                  onChange={(e) => setDeleteFeedback(e.target.value)}
                  placeholder="Your feedback is valuable and helps us improve..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={isDeleting || !deleteConfirmed}
                className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
              >
                {isDeleting ? "Submitting Request..." : "Yes, Request Permanent Deletion"}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setDeleteConfirmed(false)
                  setDeleteFeedback("")
                }}
                disabled={isDeleting}
                className="w-full px-6 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ProfileCompletionProgress({ profile, onNavigate }: { profile: any; onNavigate: (tab: string) => void }) {
  const [destinationPrefs, setDestinationPrefs] = useState<any>(null)

  useEffect(() => {
    const fetchDest = async () => {
      const user = lumi.auth.user
      if (!user) return
      try {
        const { list } = await lumi.entities.DestinationPreferences.list({
          filter: { userId: user.userId }
        })
        if (list.length > 0) setDestinationPrefs(list[0])
      } catch (error) {
        console.error(error)
      }
    }
    fetchDest()
  }, [])

  const steps = [
    { key: "fullName", label: "Personal Info", tab: "profile", check: () => !!(profile?.fullName && profile?.email && profile?.dateOfBirth && profile?.nationality) },
    { key: "educationLevels", label: "Education", tab: "profile", check: () => !!(profile?.educationLevels?.length) },
    { key: "specialties", label: "Specialties", tab: "profile", check: () => !!(profile?.specialties?.length) },
    { key: "permanentAddress", label: "Address", tab: "profile", check: () => !!(profile?.permanentAddress?.line1 && profile?.permanentAddress?.city) },
    { key: "documents", label: "Documents", tab: "documents", check: () => false },
    { key: "destinations", label: "Destinations", tab: "destinations", check: () => !!(destinationPrefs?.selectedCountries?.length > 0) }
  ]
  const completed = steps.filter(s => s.check()).length
  const progress = Math.round((completed / steps.length) * 100)
  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h3 className="font-semibold text-gray-900 mb-3">Profile Completion: {progress}%</h3>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
        <div className="bg-emerald-600 h-2 rounded-full" style={{width: `${progress}%`}}></div>
      </div>
      <div className="flex flex-wrap gap-2">
        {steps.map(step => (
          <button key={step.key} onClick={() => onNavigate(step.tab)} className="text-sm text-emerald-600 hover:underline">
            {step.check() ? "✓" : "○"} {step.label}
          </button>
        ))}
      </div>
    </div>
  )
}

